import { useMemo, useState } from 'react';
import { Button, Chip } from '@heroui/react';
import {
  packComponentParts,
  calcComponentCncCost,
  type MaterialLibrary,
  type ComponentNestingPart,
  type ComponentCncResult,
  type ComponentCncSettings,
} from '@estimator/shared';
import type { Sheet as ApiSheet } from '@/api/nesting';
import { useCompanySettings } from '@/api/company-settings';
import SheetLayoutDiagram from './SheetLayoutDiagram';

interface ComponentNestingPanelProps {
  parts: ComponentNestingPart[];
  materials: MaterialLibrary[];
  /** Priced CNC result at the company reference batch. Omit to hide CNC rows. */
  cnc?: ComponentCncResult;
}

const QTY_OPTIONS = [1, 5, 10, 25];

function pct(n: number): string {
  return `${n.toFixed(0)}%`;
}

/**
 * Diagnostic nesting view for a single component template.
 *
 * Shows how the component's panels actually sit on sheet stock so waste
 * factors can be tuned against something real instead of a flat percentage.
 *
 * Nesting ONE unit almost always opens a single mostly-empty sheet, which makes
 * "implied waste" look enormous. The quantity selector exists so the pooling
 * effect is visible — that is the number worth tuning against.
 */
export default function ComponentNestingPanel({ parts, materials, cnc }: ComponentNestingPanelProps) {
  const [unitQty, setUnitQty] = useState(1);
  const { data: companySettings } = useCompanySettings();

  const materialMap = useMemo(
    () => new Map(materials.map((m) => [m.id, m])),
    [materials],
  );

  const results = useMemo(
    () => packComponentParts(parts, materialMap, unitQty, companySettings?.kerf_width),
    [parts, materialMap, unitQty, companySettings?.kerf_width],
  );

  // What CNC would cost per unit if you really cut unitQty of them. Separate
  // from the priced figure, which is pinned to the company reference batch so
  // it stays reproducible.
  const cncAtSelectedQty = useMemo(
    () => calcComponentCncCost(
      parts,
      materialMap,
      companySettings as ComponentCncSettings | undefined,
      companySettings?.kerf_width,
      unitQty,
    ),
    [parts, materialMap, companySettings, unitQty],
  );

  const nestable = results.filter((r) => r.sheet_w > 0);
  const nonSheet = results.filter((r) => r.sheet_w === 0);

  if (results.length === 0) {
    return (
      <div className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>
        Assign materials to the panels to see a nesting preview.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          Diagnostic only — the project nesting run pools parts across every line item.
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] mr-1" style={{ color: 'var(--text-muted)' }}>Nest</span>
          {QTY_OPTIONS.map((q) => (
            <Button
              key={q}
              size="sm"
              variant={unitQty === q ? 'solid' : 'bordered'}
              className="min-w-0 h-6 px-2 text-[11px]"
              style={unitQty === q ? { backgroundColor: '#e67e22', color: '#fff' } : undefined}
              onPress={() => setUnitQty(q)}
            >
              {q}x
            </Button>
          ))}
        </div>
      </div>

      {nestable.map((r) => {
        const chargedVsNested = r.consumed_sqft > 0 ? r.sqft_with_waste / r.consumed_sqft : 0;
        const underCharging = r.sqft_with_waste < r.consumed_sqft;
        return (
          <div key={r.material_id} className="rounded-md p-2" style={{ backgroundColor: 'var(--bg-panel)' }}>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
              <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                {r.material_name}
              </span>
              <div className="flex items-center gap-1">
                <Chip size="sm" variant="flat" className="h-5 text-[10px]">
                  {r.sheet_count} sheet{r.sheet_count !== 1 ? 's' : ''}
                </Chip>
                <Chip size="sm" variant="flat" className="h-5 text-[10px]">
                  {pct(r.efficiency_pct)} full
                </Chip>
                {r.grain_locked && (
                  <Chip size="sm" variant="flat" className="h-5 text-[10px]">grain locked</Chip>
                )}
              </div>
            </div>

            <div className="text-[10px] space-y-0.5 mb-2" style={{ color: 'var(--text-muted)' }}>
              <div>
                {r.part_count} piece{r.part_count !== 1 ? 's' : ''} · {r.parts_sqft.toFixed(1)} sqft of parts ·
                {' '}{r.sheet_w}&quot;x{r.sheet_h}&quot; stock, {r.kerf}&quot; kerf
                {r.border > 0 ? `, ${r.border}" border` : ''}
              </div>
              <div>
                Charged: {r.sqft_with_waste.toFixed(1)} sqft (parts + waste) ·
                {' '}Nested: {r.consumed_sqft.toFixed(1)} sqft ({r.sheet_count} full sheet{r.sheet_count !== 1 ? 's' : ''})
              </div>
              <div style={{ color: underCharging ? '#e67e22' : 'var(--text-muted)' }}>
                Waste implied by this nest: {pct(r.implied_waste_pct)}
                {underCharging
                  ? ` — charging ${pct(chargedVsNested * 100)} of what a solo cut consumes`
                  : ' — charged area already covers the sheets opened'}
              </div>
              {r.skipped.length > 0 && (
                <div style={{ color: '#e74c3c' }}>
                  {r.skipped.length} piece{r.skipped.length !== 1 ? 's' : ''} too large for this stock — not placed.
                </div>
              )}
            </div>

            {cnc?.enabled && (() => {
              const priced = cnc.materials.find((m) => m.material_id === r.material_id);
              const diag = cncAtSelectedQty.materials.find((m) => m.material_id === r.material_id);
              if (!priced && !diag) return null;
              return (
                <div className="text-[10px] mb-2 flex flex-wrap gap-x-3 gap-y-0.5" style={{ color: 'var(--text-muted)' }}>
                  {priced && (
                    <span>
                      <span style={{ color: '#e67e22' }}>CNC priced</span>{' '}
                      ${priced.cost_per_unit.toFixed(2)}/unit · {priced.min_per_unit.toFixed(1)} min ·
                      {' '}{priced.sheets_per_unit.toFixed(2)} sheets — at batch of {cnc.batch_qty}
                    </span>
                  )}
                  {diag && unitQty !== cnc.batch_qty && (
                    <span>
                      diagnostic at {unitQty}x: ${diag.cost_per_unit.toFixed(2)}/unit
                    </span>
                  )}
                </div>
              );
            })()}

            <div className="space-y-2">
              {r.sheets.map((sheet, i) => (
                <SheetLayoutDiagram
                  key={i}
                  sheet={sheet as unknown as ApiSheet}
                  sheetIndex={i}
                  maxWidth={460}
                />
              ))}
            </div>
          </div>
        );
      })}

      {nonSheet.length > 0 && (
        <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          Not nested (bought by {nonSheet.map((r) => r.unit).filter((v, i, a) => a.indexOf(v) === i).join(' / ')}):
          {' '}
          {nonSheet.map((r) => `${r.material_name} ${r.parts_sqft.toFixed(1)} sqft`).join(', ')}
        </div>
      )}
    </div>
  );
}
